import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Paper,
  Skeleton,
  Chip,
  IconButton,
  Stack,
  alpha,
  Divider,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { DataGrid } from "@mui/x-data-grid";
import { useClients } from "../../hooks/useClients";
import { useConversations } from "../../hooks/useConversations";
import { useTypes } from "../../hooks/useTypes";
import { useChannels } from "../../hooks/useChannels";
import { useUsers } from "../../hooks/useUsers";
import { useLocation, useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import AdvancedSearchModal from "../../components/AdvancedSearchModal";

function parseSearchParams(search) {
  const params = new URLSearchParams(search);
  const q = params.get("q") || "";
  const filtersStr = params.get("filters");
  const filters = filtersStr ? filtersStr.split(",").map((s) => s.trim()).filter(Boolean) : [];
  const data_inicio = params.get("data_inicio") || "";
  const data_fim = params.get("data_fim") || "";
  const date_field = params.get("date_field") === "entrada" ? "entrada" : "atualizacao";
  const tipo_responsavel = params.get("tipo_responsavel") || "";
  const csv = (s) => (s ? s.split(",").map((x) => x.trim()).filter(Boolean) : []);
  const ids_tipos = csv(params.get("ids_tipos"));
  const ids_subtipo1 = csv(params.get("ids_subtipo1"));
  const ids_subtipo2 = csv(params.get("ids_subtipo2"));
  const ids_subtipo3 = csv(params.get("ids_subtipo3"));
  const ids_origens = csv(params.get("ids_origens"));
  const ids_usuarios = csv(params.get("ids_usuarios"));
  const include_message_text =
    (params.get("include_message_text") || "").trim().toLowerCase();
  return {
    q,
    filters,
    data_inicio,
    data_fim,
    date_field,
    tipo_responsavel,
    ids_tipos,
    ids_subtipo1,
    ids_subtipo2,
    ids_subtipo3,
    ids_origens,
    ids_usuarios,
    include_message_text,
  };
}

function flattenTypesTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => [
    { id: n.id, id_pai: n.id_pai ?? null, nome: n.nome || String(n.id) },
    ...flattenTypesTree(n.children || []),
  ]);
}

function formatDateBR(dateStr) {
  if (!dateStr) return "";
  // Se já estiver em formato brasileiro, mantém
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  // Converte de ISO (YYYY-MM-DD) para DD/MM/YYYY
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }
  // Fallback: tenta usar Date nativo
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("pt-BR");
  }
  return dateStr;
}

function getSafeDate(valueCandidates) {
  const raw = valueCandidates.find((v) => v != null && v !== "") ?? null;
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

const SearchResults = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const {
    q,
    filters,
    data_inicio,
    data_fim,
    date_field,
    tipo_responsavel,
    ids_tipos,
    ids_subtipo1,
    ids_subtipo2,
    ids_subtipo3,
    ids_origens,
    ids_usuarios,
    include_message_text,
  } = useMemo(
    () => parseSearchParams(location.search),
    [location.search]
  );

  const { data: typesData } = useTypes(1, 500, "");
  const { data: channelsData } = useChannels(1, 200, "");
  // Inclui ChatBots (ura=1) para os nomes aparecerem corretamente quando o filtro de responsável incluir bots.
  const { data: usersData } = useUsers(1, 200, "", "todos");

  const typesFlat = useMemo(
    () => flattenTypesTree(typesData?.types || []),
    [typesData?.types]
  );
  const typeIdToName = useMemo(
    () => Object.fromEntries(typesFlat.map((t) => [String(t.id), t.nome || `Tipo ${t.id}`])),
    [typesFlat]
  );
  const channelIdToName = useMemo(
    () =>
      Object.fromEntries(
        (channelsData?.channels || []).map((c) => [String(c.id), c.nome || c.name || `Canal ${c.id}`])
      ),
    [channelsData?.channels]
  );
  const userIdToName = useMemo(
    () =>
      Object.fromEntries(
        (usersData?.users || []).map((u) => [String(u.id), u.nome || u.name || `Usuário ${u.id}`])
      ),
    [usersData?.users]
  );

  useEffect(() => {
    setSearchTerm(q);
    setDebouncedSearch(q);
  }, [q]);

  const debounceSearch = useCallback(
    debounce((value) => setDebouncedSearch(value), 300),
    []
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    debounceSearch(value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      updateUrl({ q: searchTerm.trim() });
    }
  };

  const handleSearchBlur = () => {
    if (searchTerm !== q) updateUrl({ q: searchTerm.trim() });
  };

  const updateUrl = (updates) => {
    const params = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === "" || (Array.isArray(value) && value.length === 0)) params.delete(key);
      else if (Array.isArray(value)) params.set(key, value.join(","));
      else params.set(key, value);
    });
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const removeFilter = (key, valueToRemove) => {
    if (key === "filters") {
      const next = filters.filter((f) => f !== valueToRemove);
      updateUrl({ filters: next });
    } else if (key === "data_inicio" || key === "data_fim") {
      updateUrl({ [key]: "" });
    } else if (key === "ids_tipos") {
      const next = ids_tipos.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_tipos: next });
    } else if (key === "ids_subtipo1") {
      const next = ids_subtipo1.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_subtipo1: next });
    } else if (key === "ids_subtipo2") {
      const next = ids_subtipo2.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_subtipo2: next });
    } else if (key === "ids_subtipo3") {
      const next = ids_subtipo3.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_subtipo3: next });
    } else if (key === "ids_origens") {
      const next = ids_origens.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_origens: next });
    } else if (key === "ids_usuarios") {
      const next = ids_usuarios.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_usuarios: next });
    } else if (key === "tipo_responsavel") {
      updateUrl({ tipo_responsavel: "" });
    } else if (key === "include_message_text") {
      updateUrl({ include_message_text: "" });
    }
  };

  const advancedParams = useMemo(() => {
    const hasDates = data_inicio || data_fim;
    const hasIds =
      (ids_tipos?.length || 0) +
      (ids_subtipo1?.length || 0) +
      (ids_subtipo2?.length || 0) +
      (ids_subtipo3?.length || 0) +
      (ids_origens?.length || 0) +
      (ids_usuarios?.length || 0) >
      0;
    const hasTipoResponsavel =
      ["fila", "humanos", "chatbots", "agentes"].includes(tipo_responsavel);
    const includeMsg =
      include_message_text === "1" ||
      include_message_text === "true" ||
      include_message_text === "sim";

    if (!hasDates && !hasIds && !hasTipoResponsavel && !includeMsg) return null;

    return {
      ...(hasDates ? { data_inicio, data_fim, date_field } : {}),
      ...(ids_tipos?.length ? { ids_tipos } : {}),
      ...(ids_subtipo1?.length ? { ids_subtipo1 } : {}),
      ...(ids_subtipo2?.length ? { ids_subtipo2 } : {}),
      ...(ids_subtipo3?.length ? { ids_subtipo3 } : {}),
      ...(ids_origens?.length ? { ids_origens } : {}),
      ...(ids_usuarios?.length ? { ids_usuarios } : {}),
      ...(hasTipoResponsavel ? { tipo_responsavel } : {}),
      ...(includeMsg ? { include_message_text: true } : {}),
    };
  }, [
    data_inicio,
    data_fim,
    date_field,
    tipo_responsavel,
    ids_tipos,
    ids_subtipo1,
    ids_subtipo2,
    ids_subtipo3,
    ids_origens,
    ids_usuarios,
    include_message_text,
  ]);

  const advancedParamsRel = useMemo(
    () => ({
      ...(advancedParams || {}),
      tipo_scope: "relacionamento",
      default_date_window_days: 90,
    }),
    [advancedParams]
  );
  const advancedParamsCom = useMemo(
    () => ({
      ...(advancedParams || {}),
      tipo_scope: "comercial",
      default_date_window_days: 90,
    }),
    [advancedParams]
  );

  const isAtendimentoTab = tabIndex === 0;
  const isComprasTab = tabIndex === 1;
  const isClientesTab = tabIndex === 2;

  // Evita "varrer" 90 dias sem necessidade (muito caro).
  // Só busca conversas quando há termo (>=2) OU algum filtro avançado.
  const shouldFetchConversations = useMemo(() => {
    const termOk = typeof debouncedSearch === "string" && debouncedSearch.trim().length >= 2;
    return termOk || advancedParams != null;
  }, [debouncedSearch, advancedParams]);

  const { data: clientData, isLoading: loadingClients } = useClients(
    1,
    10,
    debouncedSearch,
    null,
    { enabled: isClientesTab }
  );

  const {
    data: conversationsDataRel,
    isLoading: loadingConversationsRel,
    isFetchingNextPage: isFetchingNextPageRel,
  } = useConversations(debouncedSearch, filters, advancedParamsRel, false, {
    enabled: isAtendimentoTab && shouldFetchConversations,
  });

  const {
    data: conversationsDataCom,
    isLoading: loadingConversationsCom,
    isFetchingNextPage: isFetchingNextPageCom,
  } = useConversations(debouncedSearch, filters, advancedParamsCom, false, {
    enabled: isComprasTab && shouldFetchConversations,
  });

  const handleTabChange = (_, newIndex) => {
    setTabIndex(newIndex);
  };

  const sanitizeText = (text) => {
    if (!text) return "—";
    const clean = String(text).replace(/<[^>]*>/g, "").trim();
    return clean || "—";
  };

  const conversationColumns = [
    { field: "id", headerName: "ID", width: 72, minWidth: 56 },
    { field: "name", headerName: "Nome", minWidth: 160, flex: 0.5 },
    {
      field: "lastMessage",
      headerName: "Última Mensagem",
      minWidth: 200,
      flex: 1,
      valueGetter: (value) => sanitizeText(value),
    },
    {
      field: "createdAt",
      headerName: "Data de cadastro",
      minWidth: 130,
      valueGetter: (_, row) =>
        getSafeDate([
          row?.createdAt,
          row?.created_at,
          row?.data_abertura,
          row?.data_criacao,
        ]),
    },
    {
      field: "lastUpdate",
      headerName: "Última atualização",
      minWidth: 150,
      valueGetter: (_, row) =>
        getSafeDate([
          row?.lastUpdate,
          row?.updatedAt,
          row?.updated_at,
          row?.data_ultima_atualizacao,
          row?.timestamp,
        ]),
    },
    {
      field: "typeName",
      headerName: "Tipo",
      minWidth: 120,
      flex: 0.35,
      valueGetter: (value, row) =>
        row?.typeName || (row?.typeId != null ? typeIdToName[String(row.typeId)] : null) || "—",
    },
    {
      field: "subtypesLabel",
      headerName: "Subtipos",
      minWidth: 160,
      flex: 0.4,
      valueGetter: (_, row) => {
        const names = [row?.subtype1, row?.subtype2, row?.subtype3]
          .filter((id) => id != null && id !== "")
          .map((id) => typeIdToName[String(id)])
          .filter(Boolean);
        return names.length ? names.join(" / ") : "—";
      },
    },
    { field: "statusLabel", headerName: "Status", width: 140, minWidth: 120 },
    { field: "channel", headerName: "Canal", minWidth: 140, flex: 0.4 },
  ];

  const clientColumns = [
    { field: "id", headerName: "ID", width: 72, minWidth: 56 },
    { field: "nome", headerName: "Nome", minWidth: 160, flex: 0.6 },
    { field: "email", headerName: "E-mail", minWidth: 200, flex: 1 },
    { field: "cidade", headerName: "Cidade", width: 140 },
    { field: "uf", headerName: "Estado", width: 80 },
  ];

  const flattenConversations = (data) =>
    data?.pages?.flatMap((page) => {
      if (Array.isArray(page?.list)) return page.list;
      if (Array.isArray(page?.data)) return page.data;
      if (Array.isArray(page)) return page;
      return [];
    }) || [];

  const conversationsRel = flattenConversations(conversationsDataRel);
  const conversationsCom = flattenConversations(conversationsDataCom);

  const conversationRowsRel = conversationsRel.map((conv, index) => ({
    ...conv,
    id: conv.id ?? conv.conversationId ?? `conv-rel-${index}`,
  }));
  const conversationRowsCom = conversationsCom.map((conv, index) => ({
    ...conv,
    id: conv.id ?? conv.conversationId ?? `conv-com-${index}`,
  }));

  const rawClients = clientData?.clients;
  const clients =
    (Array.isArray(rawClients)
      ? rawClients
      : Array.isArray(rawClients?.data)
      ? rawClients.data
      : []) || [];

  const clientTotalCount =
    clientData?.pagination?.total ??
    rawClients?.pagination?.total ??
    0;

  const clientRows = clients.map((client, index) => ({
    ...client,
    id: client.id ?? `client-${index}`,
  }));

  const loadingConversationsRelAny = loadingConversationsRel || isFetchingNextPageRel;
  const loadingConversationsComAny = loadingConversationsCom || isFetchingNextPageCom;

  const isLoading =
    (tabIndex === 0 && loadingConversationsRelAny) ||
    (tabIndex === 1 && loadingConversationsComAny) ||
    (tabIndex === 2 && loadingClients);

  const responsavelLabel = { fila: "Fila", humanos: "Humanos", chatbots: "ChatBots", agentes: "Agentes de IA" }[tipo_responsavel] || null;
  const hasFilterChips =
    filters?.length > 0 ||
    data_inicio ||
    data_fim ||
    tipo_responsavel ||
    ids_tipos?.length > 0 ||
    ids_subtipo1?.length > 0 ||
    ids_subtipo2?.length > 0 ||
    ids_subtipo3?.length > 0 ||
    ids_origens?.length > 0 ||
    ids_usuarios?.length > 0 ||
    include_message_text === "1" ||
    include_message_text === "true" ||
    include_message_text === "sim";

  const emptyConversationsRel =
    !loadingConversationsRelAny && tabIndex === 0 && conversationRowsRel.length === 0;
  const emptyConversationsCom =
    !loadingConversationsComAny && tabIndex === 1 && conversationRowsCom.length === 0;
  const emptyClients = !loadingClients && tabIndex === 2 && clientRows.length === 0;

  return (
    <Box
      className="tour-search-results-root"
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 0,
        minHeight: "60vh",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? undefined
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 140px)`,
      }}
    >
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: (theme) =>
            `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : alpha(theme.palette.primary.main, 0.12)}`,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.6)
              : alpha(theme.palette.primary.main, 0.02),
          boxShadow: (theme) =>
            theme.palette.mode === "dark" ? "none" : `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box className="tour-search-results-toolbar" sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "nowrap" }}>
            <Typography variant="h5" sx={{ fontWeight: 600, flexShrink: 0, minWidth: "fit-content" }}>
              Resultados da Busca
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar... (Enter atualiza)"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                onBlur={handleSearchBlur}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: (theme) => (theme.palette.mode === "dark" ? undefined : "#fff"),
                    borderRadius: 1.5,
                  },
                }}
              />
            </Box>
            <IconButton
              onClick={() => setShowAdvanced(true)}
              aria-label="Filtros avançados"
              color="primary"
              sx={{
                flexShrink: 0,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14) },
              }}
            >
              <TuneIcon />
            </IconButton>
          </Box>

          {hasFilterChips && (
            <>
              <Divider sx={{ borderColor: (theme) => alpha(theme.palette.divider, 0.6) }} />
              <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" useFlexGap>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mr: 0.5 }}
                >
                  Filtros ativos:
                </Typography>
                {filters.map((f) => (
                  <Chip
                    key={f}
                    size="small"
                    label={f}
                    onDelete={() => removeFilter("filters", f)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }}
                  />
                ))}
                {responsavelLabel && (
                  <Chip
                    size="small"
                    label={`Responsável: ${responsavelLabel}`}
                    onDelete={() => removeFilter("tipo_responsavel")}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12) }}
                  />
                )}
                {ids_tipos?.map((id) => (
                  <Chip
                    key={`t-${id}`}
                    size="small"
                    label={typeIdToName[String(id)] ?? `Tipo ${id}`}
                    onDelete={() => removeFilter("ids_tipos", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }}
                  />
                ))}
                {ids_subtipo1?.map((id) => (
                  <Chip
                    key={`s1-${id}`}
                    size="small"
                    label={typeIdToName[String(id)] ?? `Subtipo 1: ${id}`}
                    onDelete={() => removeFilter("ids_subtipo1", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08) }}
                  />
                ))}
                {ids_subtipo2?.map((id) => (
                  <Chip
                    key={`s2-${id}`}
                    size="small"
                    label={typeIdToName[String(id)] ?? `Subtipo 2: ${id}`}
                    onDelete={() => removeFilter("ids_subtipo2", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08) }}
                  />
                ))}
                {ids_subtipo3?.map((id) => (
                  <Chip
                    key={`s3-${id}`}
                    size="small"
                    label={typeIdToName[String(id)] ?? `Subtipo 3: ${id}`}
                    onDelete={() => removeFilter("ids_subtipo3", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08) }}
                  />
                ))}
                {ids_origens?.map((id) => (
                  <Chip
                    key={`o-${id}`}
                    size="small"
                    label={channelIdToName[String(id)] ?? `Canal ${id}`}
                    onDelete={() => removeFilter("ids_origens", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.info.main, 0.08) }}
                  />
                ))}
                {ids_usuarios?.map((id) => (
                  <Chip
                    key={`u-${id}`}
                    size="small"
                    label={userIdToName[String(id)] ?? `Usuário ${id}`}
                    onDelete={() => removeFilter("ids_usuarios", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.success.main, 0.08) }}
                  />
                ))}
                {data_inicio && (
                  <Chip
                    size="small"
                    label={`De: ${formatDateBR(data_inicio)}`}
                    onDelete={() => removeFilter("data_inicio")}
                    sx={{ fontWeight: 500 }}
                  />
                )}
                {data_fim && (
                  <Chip
                    size="small"
                    label={`Até: ${formatDateBR(data_fim)}`}
                    onDelete={() => removeFilter("data_fim")}
                    sx={{ fontWeight: 500 }}
                  />
                )}
              </Stack>
            </>
          )}
        </Box>
      </Paper>

      {isLoading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      <AdvancedSearchModal
        open={showAdvanced}
        onClose={() => setShowAdvanced(false)}
        initialQuery={q}
        initialAdvanced={{
          statusFilters: filters,
          filters,
          data_inicio,
          data_fim,
          date_field,
          tipo_responsavel,
          ids_tipos,
          ids_subtipo1,
          ids_subtipo2,
          ids_subtipo3,
          ids_origens,
          ids_usuarios,
          include_message_text:
            include_message_text === "1" ||
            include_message_text === "true" ||
            include_message_text === "sim",
        }}
        onApply={(payload) => {
          const updates = {
            q: payload.q ?? "",
            filters: payload.filters ?? [],
            data_inicio: payload.data_inicio ?? "",
            data_fim: payload.data_fim ?? "",
            date_field: payload.date_field ?? "atualizacao",
            tipo_responsavel: payload.tipo_responsavel ?? "",
            ids_tipos: payload.ids_tipos ?? [],
            ids_subtipo1: payload.ids_subtipo1 ?? [],
            ids_subtipo2: payload.ids_subtipo2 ?? [],
            ids_subtipo3: payload.ids_subtipo3 ?? [],
            ids_origens: payload.ids_origens ?? [],
            ids_usuarios: payload.ids_usuarios ?? [],
            include_message_text: payload.include_message_text ? "1" : "",
          };
          updateUrl(updates);
          setShowAdvanced(false);
        }}
      />

      <Tabs
        className="tour-search-results-tabs"
        value={tabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ minHeight: 40, alignSelf: "flex-start", borderBottom: 1, borderColor: "divider", mb: 0 }}
      >
        <Tab label={`Atendimento (${conversationRowsRel.length})`} sx={{ textTransform: "none", fontWeight: 500 }} />
        <Tab label={`Compras (${conversationRowsCom.length})`} sx={{ textTransform: "none", fontWeight: 500 }} />
        <Tab label={`Clientes (${clientTotalCount})`} sx={{ textTransform: "none", fontWeight: 500 }} />
      </Tabs>

      {isLoading && (
        <Paper elevation={3} sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} variant="text" width="100%" height={52} sx={{ my: 0.5 }} />
          ))}
        </Paper>
      )}

      {!isLoading && tabIndex === 0 && (
        <>
          {emptyConversationsRel ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Nenhum resultado em Atendimento.</Typography>
            </Paper>
          ) : (
            <Paper elevation={0} variant="outlined" sx={{ flex: 1, minHeight: 400 }}>
              <DataGrid
                rows={conversationRowsRel}
                columns={conversationColumns}
                autoHeight
                disableRowSelectionOnClick
                pageSizeOptions={[10, 24, 50]}
                onRowClick={(params) => navigate(`/chat/${params.row.id}`)}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-cell": { py: 1 },
                  "& .MuiDataGrid-columnHeaders": { minHeight: 44 },
                  "& .MuiDataGrid-row": { cursor: "pointer" },
                }}
              />
              {isFetchingNextPageRel && (
                <Box sx={{ py: 1, textAlign: "center" }}>
                  <Skeleton variant="text" width="100%" height={32} />
                </Box>
              )}
            </Paper>
          )}
        </>
      )}

      {!isLoading && tabIndex === 1 && (
        <>
          {emptyConversationsCom ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Nenhum resultado em Compras.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Solicitações comerciais aparecem no Pipeline de vendas.
              </Typography>
            </Paper>
          ) : (
            <Paper elevation={0} variant="outlined" sx={{ flex: 1, minHeight: 400 }}>
              <DataGrid
                rows={conversationRowsCom}
                columns={conversationColumns}
                autoHeight
                disableRowSelectionOnClick
                pageSizeOptions={[10, 24, 50]}
                onRowClick={(params) => navigate(`/chat/${params.row.id}`)}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-cell": { py: 1 },
                  "& .MuiDataGrid-columnHeaders": { minHeight: 44 },
                  "& .MuiDataGrid-row": { cursor: "pointer" },
                }}
              />
              {isFetchingNextPageCom && (
                <Box sx={{ py: 1, textAlign: "center" }}>
                  <Skeleton variant="text" width="100%" height={32} />
                </Box>
              )}
            </Paper>
          )}
        </>
      )}

      {!isLoading && tabIndex === 2 && (
        <>
          {emptyClients ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Nenhum cliente encontrado.</Typography>
            </Paper>
          ) : (
            <Paper elevation={0} variant="outlined" sx={{ flex: 1, minHeight: 400 }}>
              <DataGrid
                rows={clientRows}
                columns={clientColumns}
                autoHeight
                disableRowSelectionOnClick
                pageSizeOptions={[10, 24, 50]}
                onRowClick={(params) => navigate(`/clients/${params.row.id}`)}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-cell": { py: 1 },
                  "& .MuiDataGrid-columnHeaders": { minHeight: 44 },
                  "& .MuiDataGrid-row": { cursor: "pointer" },
                }}
              />
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default SearchResults;
